import React, {
  ChangeEvent,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import Textarea, {
  Props as TextareaProps,
} from 'pages/Dashboard/components/Input/Textarea';
import {
  VoiceRecognitionData,
} from 'pages/Dashboard/hooks/useSpeechToText';
import isNil from 'lodash/isNil';
import Mic from 'pages/Dashboard/components/Mic';
import {
  notEmptyString,
} from '@populateui/utils';

type Props = TextareaProps & {
  section?: string;
  patientId?: number;
  hasHPI?: boolean;
  visitNoteId?: number;
  encounterId?: number;
  trackingLabel?: string;
  containerClassName?: string;
};

export default function TextAreaWithActions({
  section,
  patientId,
  hasHPI,
  id,
  visitNoteId,
  encounterId,
  nonEditable,
  onChange,
  value = '',
  trackingLabel,
  containerClassName,
  ...props
}: Props) {
  const [speechToText, setSpeechToText] = useState<VoiceRecognitionData>();
  const {
    unprocessedText,
    text,
    loading,
    listening,
    startVoiceRecognition,
    stopVoiceRecognition,
  } = speechToText ?? {};
  const unprocessedTextElRef = useRef<HTMLSpanElement>(null);
  const [cursor, setCursor] = useState<number[]>([]);

  const containerClasses = clsx(containerClassName, 'flex-col');

  const handleMicClick = (e?: MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    if (listening) {
      stopVoiceRecognition?.();
    } else {
      startVoiceRecognition?.();
    }
  };

  const getTextInSelectedPosition = (text: string, adjustCursor: boolean): string => {
    const _value = value?.toString() ?? '';
    const [selectionStart = 0, selectionEnd = 0] = cursor;
    const prefix = _value?.slice?.(0, selectionStart);
    const separator = prefix?.slice?.(-1) === '\n' ? '' : ' ';
    const prefixedText = compact([prefix, text]).join(separator);
    const suffix = _value?.slice?.(selectionEnd);
    if (adjustCursor) {
      setCursor([prefixedText.length, prefixedText.length]);
    }
    return [prefixedText, suffix].join(' ');
  };

  const handleTextChange = (text: string) => {
    const _value = getTextInSelectedPosition(text, true);
    onChange(_value?.replaceAll(' .', '.'));
  };

  const adjustedUnprocessedText: string = useMemo(
    () => getTextInSelectedPosition(unprocessedText ?? '', false),
    [unprocessedText, cursor],
  );

  useEffect(() => {
    if (!isNil(text) && text.length > 0 && listening) {
      handleTextChange(text);
    }
  }, [text, listening]);

  useEffect(() => {
    const element = unprocessedTextElRef.current;
    if (element) {
      element.scrollTop = element.scrollHeight ?? 0;
    }
  }, [unprocessedText]);

  useEffect(() => {
    const len = value?.toString()?.length ?? 0;
    if (isEmpty(cursor) && len > 0) {
      setCursor([len, len]);
    }
  }, [value, cursor]);

  return (
    (nonEditable && notEmptyString(value)) || !nonEditable)
    ? (
      <Textarea
        id={id}
        textAreaClassName=""
        containerClassName={containerClasses}
        nonEditable={nonEditable}
        onChange={onChange}
        value={value}
        allowSpeechToText={false}
        onSelect={(e: ChangeEvent<HTMLTextAreaElement>) => {
          const { selectionStart = 0, selectionEnd = 0 } = e.target;
          setCursor([selectionStart, selectionEnd]);
        }}
        readonly={nonEditable}
        {...props}
        rows={nonEditable ? 0 : props.rows}
        trackingLabel={trackingLabel}
        speechToText={(data) => { setSpeechToText(data); }}
      >
        {listening && unprocessedText && (
          <span
            ref={unprocessedTextElRef}
            className="w-full h-full absolute bg-gray-50 border-gray-400 text-xs pl-3 pt-2 pb-6 overflow-y-scroll whitespace-pre-wrap"
          >
            {adjustedUnprocessedText}
          </span>
        )}
        {(!nonEditable && !props.disabled) && (
          <div className="flex -space-x-1.5">
            <Mic
              listening={listening}
              loading={loading}
              onBlur={stopVoiceRecognition}
              onFocus={startVoiceRecognition}
              onClick={handleMicClick}
              micClasses="nw-dictation-button"
            />
          </div>
        )}
      </Textarea>
    ) : <div />;
}
